import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Car, 
  Calendar, 
  MapPin, 
  User, 
  Phone, 
  Mail, 
  Clock, 
  CreditCard,
  CheckCircle,
  Loader2,
  ArrowLeft
} from 'lucide-react';

const LOGO_URL = "/onyx-reserve-logo.png";

const BookSUV = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const hours = parseInt(searchParams.get('hours')) || 4;
  const total = parseInt(searchParams.get('total')) || 600;
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [paymentLink, setPaymentLink] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    pickup_location: '',
    notes: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.date || !formData.time || !formData.pickup_location) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // Create the hourly booking ride
      const response = await axios.post(`${API}/public/book-suv`, {
        ...formData,
        hours,
        total_amount: total,
        vehicle_type: 'Luxury SUV'
      });
      
      setPaymentLink(response.data.payment_link);
      setSubmitted(true);
      toast.success('Booking submitted! Check your email for the payment link.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-black py-4 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <img src={LOGO_URL} alt="Onyx Reserve" className="h-12" />
            <Button variant="outline" className="text-white border-white hover:bg-white hover:text-black" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </header>

        <div className="max-w-2xl mx-auto py-16 px-4">
          <Card className="text-center">
            <CardContent className="pt-8">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Booking Submitted!</h2>
              <p className="text-gray-600 mb-6">
                Thank you, {formData.name}! Your {hours}-hour Luxury SUV booking has been received.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
                <h3 className="font-semibold text-gray-900 mb-4">Booking Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Vehicle:</span>
                    <span className="font-medium">Black Luxury SUV</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Duration:</span>
                    <span className="font-medium">{hours} Hours</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date:</span>
                    <span className="font-medium">{formData.date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Time:</span>
                    <span className="font-medium">{formData.time}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-semibold">Total:</span>
                    <span className="font-bold text-green-600">${total}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gold-50 border border-gold-200 rounded-lg p-4 mb-6">
                <p className="text-gold-800 text-sm">
                  <strong>Next Step:</strong> A secure payment link has been sent to <strong>{formData.email}</strong>. 
                  Please complete your payment to confirm your booking.
                </p>
              </div>

              {paymentLink && (
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700 mb-4"
                  onClick={() => window.open(paymentLink, '_blank')}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pay Now - ${total}
                </Button>
              )}

              <Button variant="outline" className="w-full" onClick={() => navigate('/')}>
                Back to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <img src={LOGO_URL} alt="Onyx Reserve" className="h-12" />
          <Button variant="outline" className="text-white border-white hover:bg-white hover:text-black" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto py-12 px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Book Your Luxury SUV</h1>
          <p className="text-gray-600">Complete your {hours}-hour luxury SUV booking</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Booking Details</CardTitle>
                <CardDescription>Fill in your information to complete the booking</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Contact Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="John Smith"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(305) 555-0123"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john@email.com"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Payment link will be sent to this email</p>
                    </div>
                  </div>

                  {/* Date & Time */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Date & Time
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="date">Pickup Date *</Label>
                        <Input
                          id="date"
                          name="date"
                          type="date"
                          value={formData.date}
                          onChange={handleInputChange}
                          min={new Date().toISOString().split('T')[0]}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="time">Pickup Time *</Label>
                        <Input
                          id="time"
                          name="time"
                          type="time"
                          value={formData.time}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Pickup Location */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <MapPin className="h-5 w-5" />
                      Pickup Location
                    </h3>
                    <div>
                      <Label htmlFor="pickup_location">Address *</Label>
                      <Input
                        id="pickup_location"
                        name="pickup_location"
                        value={formData.pickup_location}
                        onChange={handleInputChange}
                        placeholder="123 Main St, Miami, FL"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="notes">Special Instructions (optional)</Label>
                      <Input
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        placeholder="Gate code, specific instructions, etc."
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-gold-500 hover:bg-gold-400 text-black font-semibold"
                    disabled={loading}
                    size="lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" />
                        Continue to Payment - ${total}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-4">
              <CardHeader className="bg-black text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <Car className="h-5 w-5" />
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_9f390971-c0d4-4d2a-a399-34c5105bc66f/artifacts/qzo5rung_IMG_2559.jpeg" 
                      alt="Black Luxury SUV"
                      className="w-20 h-14 object-cover rounded"
                    />
                    <div>
                      <p className="font-semibold">Black Luxury SUV</p>
                      <p className="text-sm text-gray-500">Luxury SUV</p>
                    </div>
                  </div>

                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-medium">{hours} Hours</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Rate:</span>
                      <span className="font-medium">${total / hours}/hour</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Passengers:</span>
                      <span className="font-medium">Up to 6</span>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex justify-between">
                      <span className="font-semibold text-lg">Total</span>
                      <span className="font-bold text-2xl text-green-600">${total}</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <p className="text-sm font-medium text-gray-900">Includes:</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Professional chauffeur
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Luxury leather interior
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Complimentary water
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        24/7 support
                      </li>
                    </ul>
                  </div>

                  <p className="text-xs text-gray-500 text-center">
                    * Fuel and gratuity not included
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookSUV;
